import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, zip, map, tap, finalize } from 'rxjs';

import { Store } from '@ngxs/store';
import { FieldDef } from 'app/core/models';
import { RelatedToType } from 'app/shared/enums';
import { AccountService } from 'app/core/services';
import { ObjectsManagerActions } from 'app/modules/settings/objects-manager/state/actions';
import {
  CustomizableFieldSaveModel,
  FieldDefCustomization,
  FieldDefCustomizationCreate,
  LockableFieldSaveModel,
  LockedField,
  LockedFieldCreate,
  LockedFieldsResponse,
} from '../locked-fields.types';

@Injectable()
export class LockedFieldService {
  private _accountId: string;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly accountService: AccountService,
    private readonly store: Store,
  ) {
    this._accountId = this.accountService.getCurrentAccount().id;
  }

  getByAccountId(accountId: string): Observable<LockedField[]> {
    return this.httpClient.get(`locked_fields`).pipe(map((response: LockedFieldsResponse) => response.locked_fields));
  }

  private delete(id: number, accountId: string): Observable<any> {
    return this.httpClient.delete(`locked_fields/${id}`);
  }

  private create(model: LockedFieldCreate): Observable<LockedField[]> {
    const { accountId, fields, relatedToType } = model;
    const key = model.role_id ? 'role_id' : 'user_id';
    const data = { fields: fields.map((f) => [relatedToType, f]), [key]: model[key] };

    return this.httpClient
      .post<any>(`locked_fields`, data)
      .pipe(map((response: LockedFieldsResponse) => response.locked_fields));
  }

  private createFieldDefCustomization(model: FieldDefCustomizationCreate) {
    return this.httpClient.post<any>(`field_def_customizations`, model);
  }

  save(model: LockableFieldSaveModel): Observable<LockedField[][]> {
    const { accountId, fields, relatedToType, roles, users } = model;
    const data = { fields, relatedToType, accountId };
    let toRemove = [...users.toRemove, ...roles.toRemove].map((id) => this.delete(id, accountId));
    let toAdd = [
      ...users.toAdd.map((user_id) => this.create({ ...data, user_id })),
      ...roles.toAdd.map((role_id) => this.create({ ...data, role_id })),
    ];

    toRemove = toRemove?.length ? toRemove : [of(null)];
    toAdd = toAdd?.length ? toAdd : [of(null)];

    return zip(forkJoin(toRemove), forkJoin(toAdd)).pipe(
      map(([, lockedFields]: [any[], LockedField[][]]) => lockedFields),
    );
  }

  saveCustomizableFields(model: CustomizableFieldSaveModel): Observable<any[]> {
    const { fields, customization_name, customization_value, field_def_related_to_type, users, roles } = model;

    const basePayload = {
      fields,
      customization_name,
      field_def_related_to_type,
    };

    const activateCalls = [
      ...users.toAdd.map((userId) =>
        this.createFieldDefCustomization({
          ...basePayload,
          customization_value: true,
          related_to_type: 'User',
          related_to_id: userId,
        }),
      ),
      ...roles.toAdd.map((roleId) =>
        this.createFieldDefCustomization({
          ...basePayload,
          customization_value: true,
          related_to_type: 'Role',
          related_to_id: roleId,
        }),
      ),
    ];

    const deactivateCalls = [
      ...users.toRemove.map((userId) =>
        this.createFieldDefCustomization({
          ...basePayload,
          customization_value: null,
          related_to_type: 'User',
          related_to_id: userId,
        }),
      ),
      ...roles.toRemove.map((roleId) =>
        this.createFieldDefCustomization({
          ...basePayload,
          customization_value: null,
          related_to_type: 'Role',
          related_to_id: roleId,
        }),
      ),
    ];

    const calls = [...activateCalls, ...deactivateCalls];

    if (calls.length === 0) {
      return of([]);
    }
    return forkJoin(calls);
  }

  toggle(mode: 'visibility' | 'history' | 'inherited', fieldDef: FieldDef): Observable<Array<FieldDef>> {
    const routeMap = {
      visibility: 'toggle_hidden_field',
      history: 'toggle_show_history_field',
      inherited: 'toggle_inherited_field',
    };

    const getCustomFieldUpdate = (mode: 'visibility' | 'history' | 'inherited', fieldDef: FieldDef) => {
      switch (mode) {
        case 'history':
          return { show_history: !fieldDef.show_history };
        case 'visibility':
          return { archived: !fieldDef.archived };
        case 'inherited':
          return { inherited: !fieldDef.inherited };
        default:
          return {};
      }
    };

    const customFieldConfig = {
      url: `custom_fields/${fieldDef.id}`,
      data: {
        custom_field: getCustomFieldUpdate(mode, fieldDef),
      },
    };

    const standardFieldConfig = {
      url: `fields/${routeMap[mode]}`,
      data: {
        standard_field_name: fieldDef.name,
        related_to_type: fieldDef.related_to_type,
      },
    };

    const config = fieldDef.meta?.is_custom ? customFieldConfig : standardFieldConfig;

    this.accountService.fieldDefsCacheBuster();

    return this.httpClient
      .put<Array<FieldDef>>(config.url, config.data)
      .pipe(finalize(() => this.store.dispatch(new ObjectsManagerActions.AddOrUpdateObjectField(fieldDef))));
  }

  private getAction(type: 'visibility' | 'history' | 'inherited', enable: boolean): string {
    const map = {
      visibility: enable ? 'enable_archived' : 'disable_archived',
      history: enable ? 'enable_show_history' : 'disable_show_history',
      inherited: enable ? 'enable_inherited' : 'disable_inherited',
    };
    return map[type];
  }

  batchToggle(
    type: 'visibility' | 'history' | 'inherited',
    relatedToType: string,
    fields: FieldDef[],
    enable: boolean,
  ): Observable<Array<FieldDef>> {
    const url = `accounts/${this._accountId}/field_settings`;
    const action = this.getAction(type, enable);
    const payload = {
      related_to_type: relatedToType,
      field_action: action,
      field_names: fields.map((field) => field.name),
    };

    this.accountService.fieldDefsCacheBuster();

    return this.httpClient.post<FieldDef[]>(url, payload).pipe(
      tap((updatedFields: FieldDef[]) => {
        fields.forEach((f) => this.store.dispatch(new ObjectsManagerActions.AddOrUpdateObjectField(f)));
      }),
    );
  }

  /**
   * WIP: Following code keep the field updates concise by using the same /fields API.
   * We will remove the above 'toggle' method and replace with this when the BE is ready.
   */
  // toggle(mode: 'visibility' | 'history' | 'inherited', fieldDef: FieldDef): Observable<FieldDef> {
  //   const observable$ = fieldDef.meta?.is_custom
  //     ? this.accountService.updateCustomField
  //     : this.accountService.updateStandardField;

  //   let updatedField = {
  //     ...fieldDef,
  //   };

  //   switch (mode) {
  //     case 'history':
  //       updatedField.show_history = !fieldDef.show_history;
  //       break;
  //     case 'visibility':
  //       updatedField.hidden = !fieldDef.hidden;
  //       break;
  //     case 'inherited':
  //       updatedField.inherited = !fieldDef.inherited;
  //       break;
  //   }

  //   return observable$(updatedField).pipe(
  //     tap((field) => this.store.dispatch(new ObjectsManagerActions.AddOrUpdateObjectField(field))),
  //     finalize(() => this.accountService.fieldDefsCacheBuster()),
  //   );
  // }

  public importFields(
    file: File,
    relatedToType: RelatedToType,
  ): Observable<{ names: string[]; fields: FieldDef[]; errors: { [index: number]: string } }> {
    const body = new FormData();
    body.append('file', file);
    body.append('related_to_type', relatedToType);

    return this.httpClient.post<{ names: string[]; fields: FieldDef[]; errors: any[] }>(`custom_fields/import`, body);
  }

  public getFieldDefCustomizations(): Observable<FieldDefCustomization[]> {
    return this.httpClient
      .get<{ field_def_customizations: FieldDefCustomization[] }>(`field_def_customizations`)
      .pipe(map((response) => response.field_def_customizations));
  }
}
